// Playground.tsx — dashboard playground chat UI (spec D6/D9, M3 task-6). Talks to
// `POST /api/bots/:botId/playground/chat` via `streamSSE` (src/lib/sse.ts) and renders the
// `token | citation | error | done` frame contract from `apps/api/src/routes/playground.ts`.
// Without AI credentials configured on the server, the engine's stub yields a single `error`
// frame ("AI is not configured on this server yet.") — that's the expected, verified path
// until M3 task-9 wires real creds; the UI treats it like any other error frame.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { botSettingsSchema } from '@repo/validation';
import { Button } from '@repo/ui/components/button';
import { SEARCHING_STATUS, resolvePlaygroundLocale } from '@repo/shared-types';
import type { BotDto, Chunk, EngineDone, EngineEvent } from '@repo/shared-types';
import { PageHeader } from '../components/PageHeader';
import { AssistantBubble, ErrorBubble, UserBubble } from '../components/chat-bubbles';
import { apiFetch } from '../lib/api';
import { streamSSE } from '../lib/sse';
import { useT } from '../locales/context';

// Derived from the canonical `EngineEvent` union (packages/shared-types/src/engine.ts)
// rather than hand-duplicated — these are exactly the shapes the SSE frames carry on the
// wire (routes/playground.ts forwards `citation`/`done` payloads through unchanged).
type Citation = Extract<EngineEvent, { type: 'citation' }>['citation'];

type ChatMessage =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'error'; text: string }
  | {
      id: string;
      role: 'assistant';
      text: string;
      citations: Citation[];
      unanswered: boolean;
      leadCaptured: boolean;
    };

function makeSessionKey(): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `pg-${Date.now().toString(36)}${random}`;
}

export function Playground() {
  const { botId } = useParams<{ botId: string }>();
  const queryClient = useQueryClient();
  const t = useT();

  // Rotating-status spec (2026-08-22) — only the bot's `settings.language` is needed here (to
  // pick which locale's `SEARCHING_STATUS` sentences to rotate through), but there is no
  // narrower endpoint than the full bot fetch `BotAppearance.tsx` already uses for the same
  // field. `enabled: !!botId` mirrors that screen; a missing/failed fetch just falls back to
  // `'en'` below rather than blocking the chat UI on it.
  const { data: bot } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
  });
  const searchingSentences = useMemo(() => {
    const language = bot ? botSettingsSchema.parse(bot.settings ?? {}).language : 'auto';
    return SEARCHING_STATUS[resolvePlaygroundLocale(language)];
  }, [bot]);

  const [sessionKey, setSessionKey] = useState(makeSessionKey);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [streamingCitations, setStreamingCitations] = useState<Citation[]>([]);
  // Rotating-status spec (2026-08-22) — set to one sentence picked at random (`Math.random()`,
  // not seeded) from `searchingSentences` the instant a `status:searching` frame arrives,
  // cleared the instant the first `token` frame arrives (same instant `streamingText` stops
  // being `''`), so `AssistantBubble` only ever shows it while there's genuinely nothing else
  // to show yet.
  const [streamingStatusText, setStreamingStatusText] = useState<string | undefined>(undefined);
  const [debugChunks, setDebugChunks] = useState<Chunk[] | null>(null);
  const [debugOpenMobile, setDebugOpenMobile] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamingText]);

  // Abort an in-flight stream if the user navigates away mid-response.
  useEffect(() => () => abortRef.current?.abort(), []);

  function handleNewSession() {
    abortRef.current?.abort();
    abortRef.current = null;
    setSessionKey(makeSessionKey());
    setMessages([]);
    setStreamingText(null);
    setStreamingCitations([]);
    setStreamingStatusText(undefined);
    setDebugChunks(null);
    setIsSending(false);
  }

  function handleAbort() {
    abortRef.current?.abort();
  }

  async function sendMessage(text: string) {
    if (!botId) return;
    const controller = new AbortController();
    abortRef.current = controller;

    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', text }]);
    setIsSending(true);
    setStreamingText('');
    setStreamingCitations([]);
    setStreamingStatusText(undefined);

    // Accumulated locally (not via state) so the `done` handler always has the exact final
    // text/citations regardless of React's async state-update batching.
    let accText = '';
    let accCitations: Citation[] = [];
    let accLeadCaptured = false;

    try {
      for await (const frame of streamSSE(
        `/api/bots/${botId}/playground/chat`,
        { message: text, sessionKey },
        { signal: controller.signal },
      )) {
        if (frame.event === 'token') {
          accText += (frame.data as { text: string }).text;
          setStreamingText(accText);
          setStreamingStatusText(undefined);
        } else if (frame.event === 'citation') {
          accCitations = [...accCitations, frame.data as Citation];
          setStreamingCitations(accCitations);
        } else if (frame.event === 'status') {
          // Fix round 1 (2026-08-22) — gate on the one known phase rather than casting
          // whatever the wire sends straight into state; a future phase this build doesn't
          // know about should be ignored, not mislabeled with today's one string.
          const { phase } = frame.data as { phase?: string };
          if (phase === 'searching') {
            setStreamingStatusText(
              searchingSentences[Math.floor(Math.random() * searchingSentences.length)],
            );
          }
        } else if (frame.event === 'lead') {
          // Playground-product-demo (2026-08-22) — the turn captured a lead. Accumulated
          // locally like text/citations so the `done` handler carries it onto the message,
          // rather than a separate state the batching could reorder.
          accLeadCaptured = true;
        } else if (frame.event === 'error') {
          const message = (frame.data as { message: string }).message;
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: 'error', text: message },
          ]);
        } else if (frame.event === 'done') {
          const done = frame.data as EngineDone;
          setMessages((prev) => [
            ...prev,
            {
              id: done.messageId ?? crypto.randomUUID(),
              role: 'assistant',
              text: accText,
              citations: accCitations,
              unanswered: done.unanswered,
              leadCaptured: accLeadCaptured,
            },
          ]);
          setDebugChunks(done.chunks ?? []);
          // Task 2 (dashboard-shell feedback cycle) — a completed turn changes Overview's
          // message count/sparkline and, when `done.unanswered`, the unanswered total too.
          // Invalidated unconditionally (not just on `unanswered`) since the message-count
          // change is unconditional. Same pattern `useAnswerUnanswered` uses for the rail's
          // pill — nothing here waits on the response, this is fire-and-forget.
          void queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] });
        }
      }
    } catch (err) {
      const aborted = err instanceof Error && err.name === 'AbortError';
      if (!aborted) {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'error',
            text: err instanceof Error ? err.message : t.playground.genericError,
          },
        ]);
      }
    } finally {
      setIsSending(false);
      setStreamingText(null);
      setStreamingCitations([]);
      setStreamingStatusText(undefined);
      abortRef.current = null;
    }
  }

  function handleSubmit() {
    const text = input.trim();
    if (!text || isSending) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    void sendMessage(text);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    const el = textareaRef.current;
    if (el) {
      // `scrollHeight` excludes the border; with `box-sizing: border-box` that left the box
      // 2px short and showed a scrollbar on a single line. Scroll only past the cap.
      el.style.height = 'auto';
      const needed = el.scrollHeight + (el.offsetHeight - el.clientHeight);
      el.style.height = `${Math.min(needed, 200)}px`;
      el.style.overflowY = needed > 200 ? 'auto' : 'hidden';
    }
  }

  const showEmptyState = messages.length === 0 && streamingText === null;

  return (
    <div className="flex h-[75vh] flex-col gap-4">
      {/* <sm: actions stack UNDER the header so "Playground" and its subline keep the full
          viewport width (Johnny's phone: the two buttons squeezed the title to "Emb…" before
          the rail took over navigation). ≥sm restores the side-by-side row. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 sm:flex-1">
          <PageHeader title={t.playground.pageTitle} subtitle={t.playground.pageSubtitle} />
        </div>
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto sm:pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="lg:hidden"
            onClick={() => setDebugOpenMobile((v) => !v)}
          >
            {debugOpenMobile ? t.playground.hideDebugCta : t.playground.retrievalDebugLabel}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleNewSession}>
            {t.playground.newSessionCta}
          </Button>
        </div>
      </div>

      {/* Mobile: explicit rows so the chat pane (row 1, minmax(0,1fr)) can actually shrink
       * to make room for the debug panel (row 2, auto + capped height) when both are
       * stacked in a single column — without this, "auto" grid rows would just grow to
       * content height and the overflow-hidden container would clip the debug panel
       * instead of letting it show below the fold. ≥lg collapses back to one shared row
       * with the original two-column layout. */}
      <div className="grid flex-1 grid-rows-[minmax(0,1fr)_auto] gap-4 overflow-hidden lg:grid-cols-[1fr_320px] lg:grid-rows-1">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border">
          <div ref={scrollRef} className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
            {showEmptyState && (
              <p className="text-sm text-muted-foreground">{t.playground.emptyStateText}</p>
            )}
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {streamingText !== null && (
              <AssistantBubble
                text={streamingText}
                citations={streamingCitations}
                statusText={streamingStatusText}
                streaming
              />
            )}
          </div>

          <div className="border-t p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder={t.playground.inputPlaceholder}
                className="max-h-[200px] flex-1 resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
              {isSending ? (
                <Button type="button" variant="destructive" onClick={handleAbort}>
                  {t.playground.stopCta}
                </Button>
              ) : (
                <Button type="button" onClick={handleSubmit} disabled={!input.trim()}>
                  {t.playground.sendCta}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div
          className={`${debugOpenMobile ? 'block' : 'hidden'} max-h-[50vh] overflow-y-auto rounded-xl border p-4 lg:block lg:max-h-none`}
        >
          <h2 className="mb-3 text-sm font-semibold">{t.playground.retrievalDebugLabel}</h2>
          {!debugChunks || debugChunks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.playground.debugEmptyText}</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {debugChunks.map((chunk, i) => (
                <li key={`${chunk.sourceUrl}-${i}`} className="rounded-md border p-2 text-xs">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span
                      className="font-mono font-medium"
                      style={{
                        color: chunk.score >= 0.8 ? 'var(--st-ready)' : 'var(--st-paused)',
                      }}
                    >
                      {chunk.score.toFixed(2)}
                    </span>
                    <a
                      href={chunk.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={chunk.sourceUrl}
                      className="truncate text-primary underline-offset-4 hover:underline"
                    >
                      {chunk.sourceUrl}
                    </a>
                  </div>
                  <p className="text-muted-foreground">{chunk.text.slice(0, 200)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  if (message.role === 'user') return <UserBubble text={message.text} />;
  if (message.role === 'error') return <ErrorBubble text={message.text} />;
  return (
    <AssistantBubble
      text={message.text}
      citations={message.citations}
      unanswered={message.unanswered}
      leadCaptured={message.leadCaptured}
    />
  );
}
